import { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Pause, Play, RefreshCw } from 'lucide-react';
import type { ArchetypeId } from '@llura/types';

import { Button, Card } from '@llura/ui';

import { useStrings } from '../../../hooks/useStrings';
import { useHaptic } from '../../../hooks/useHaptic';
import { FlatHandIllustration } from './illustrations/flat-hand-illustration';
import { OrbitIllustration } from './illustrations/orbit-illustration';

const PHASE_MS = 4000; // 4s per Contract / Relax cycle half
const KEGEL_DURATION_S = 60;

interface Level2PelvicPowerProps {
  archetypeId: ArchetypeId;
  onComplete: () => void;
}

type PelvicPhase = 'technique' | 'kegel';

interface Technique {
  id: 'flat-hand' | 'orbit';
  name: string;
  description: string;
  steps: string[];
}

const TECHNIQUES: Technique[] = [
  {
    id: 'flat-hand',
    name: 'The Flat Hand',
    description:
      'Broad pressure, full surface contact. Builds anticipation through warmth, not friction.',
    steps: [
      'Warm the hand. Slow, deep breath.',
      'Place flat — palm down, fingers spread.',
      'Press in slow waves, not strokes.',
      'Hold for three breaths. Release. Repeat.',
    ],
  },
  {
    id: 'orbit',
    name: 'The Orbit',
    description:
      'A teasing circular path around the most sensitive zone — never touching it directly.',
    steps: [
      'Trace a wide, slow circle.',
      'Tighten the arc with each loop.',
      'Skip the center. Let it ache for you.',
      'After eight orbits, change direction.',
    ],
  },
];

/**
 * Level 2 — Muscle & Technique.
 *
 * Two-phase ritual: the touch technique screen first (sensation, body
 * awareness), then a 60-second pelvic-floor pulse (Kegel — strength) on top
 * of that awareness. Both phases must finish before `onComplete` fires. Top
 * of the layout shows a small two-dot progress indicator so the user always
 * knows where they are.
 */
export function Level2PelvicPower({ archetypeId, onComplete }: Level2PelvicPowerProps) {
  void archetypeId; // reserved for future per-archetype technique variants
  const { strings } = useStrings();
  const [phase, setPhase] = useState<PelvicPhase>('technique');

  return (
    <div className="flex flex-col items-center gap-6 pt-2">
      <div className="text-center">
        <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">
          {strings.levels.pelvicPower.label}
        </p>
        <h3 className="mt-1 font-serif text-2xl">{strings.levels.pelvicPower.title}</h3>
        <p className="mt-1 max-w-xs text-sm text-muted-foreground">
          {strings.levels.pelvicPower.hint}
        </p>
        <PhaseDots phase={phase} />
      </div>

      <AnimatePresence mode="wait">
        {phase === 'technique' ? (
          <motion.div
            key="technique"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.4 }}
            className="flex w-full flex-col items-center gap-6"
          >
            <TechniquePhase onAdvance={() => setPhase('kegel')} />
          </motion.div>
        ) : (
          <motion.div
            key="kegel"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.4 }}
            className="flex w-full flex-col items-center gap-6"
          >
            <PelvicPulse onComplete={onComplete} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function PhaseDots({ phase }: { phase: PelvicPhase }) {
  const { strings } = useStrings();
  return (
    <div className="mt-3 flex items-center justify-center gap-2 text-[10px] uppercase tracking-[0.22em]">
      <span
        className={
          phase === 'technique' ? 'text-foreground' : 'text-muted-foreground line-through decoration-1'
        }
      >
        {strings.levels.pelvicPower.phaseTechnique}
      </span>
      <span aria-hidden className="text-muted-foreground">
        ·
      </span>
      <span className={phase === 'kegel' ? 'text-foreground' : 'text-muted-foreground'}>
        {strings.levels.pelvicPower.phaseKegel}
      </span>
    </div>
  );
}

/** Pelvic-floor pulse — squeeze on contract, release on relax. */
function PelvicPulse({ onComplete }: { onComplete: () => void }) {
  const { strings } = useStrings();
  const [running, setRunning] = useState(false);
  const [remaining, setRemaining] = useState(KEGEL_DURATION_S);
  const [tick, setTick] = useState(0);
  const tickRef = useRef<number | null>(null);

  useEffect(() => {
    if (!running) return;
    if (remaining <= 0) {
      setRunning(false);
      onComplete();
      return;
    }
    tickRef.current = window.setTimeout(() => {
      setRemaining((r) => r - 1);
      setTick((t) => t + 1);
    }, 1000);
    return () => {
      if (tickRef.current !== null) window.clearTimeout(tickRef.current);
    };
  }, [running, remaining, onComplete]);

  const mm = String(Math.floor(remaining / 60)).padStart(2, '0');
  const ss = String(remaining % 60).padStart(2, '0');
  // Same phase math drives both the cue text and the ring scale so they can
  // never drift: the ring grows while contracting, relaxes while releasing.
  const contracting = Math.floor(tick / (PHASE_MS / 1000)) % 2 === 0;
  const cue = useMemo(
    () => (contracting ? strings.levels.kegel.contract : strings.levels.kegel.relax),
    [contracting, strings.levels.kegel.contract, strings.levels.kegel.relax],
  );

  const reset = () => {
    setRunning(false);
    setRemaining(KEGEL_DURATION_S);
    setTick(0);
  };

  return (
    <div className="flex w-full flex-col items-center gap-6">
      <div className="relative flex h-72 w-72 items-center justify-center">
        <motion.div
          aria-hidden
          className="absolute inset-6 rounded-full bg-primary/15 blur-2xl"
          animate={
            running
              ? { scale: contracting ? 1.45 : 1, opacity: contracting ? 0.85 : 0.45 }
              : { scale: 1, opacity: 0.45 }
          }
          transition={{ duration: PHASE_MS / 1000, ease: 'easeInOut' }}
        />
        <motion.div
          className="relative flex h-56 w-56 items-center justify-center rounded-full border-2 border-primary/80 bg-card glow-primary"
          animate={running ? { scale: contracting ? 1.18 : 1 } : { scale: 1 }}
          transition={{ duration: PHASE_MS / 1000, ease: 'easeInOut' }}
        >
          <motion.span
            key={cue + tick}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="font-serif text-3xl text-foreground"
          >
            {running ? cue : strings.levels.common.ready}
          </motion.span>
        </motion.div>
      </div>

      <div className="text-center font-mono text-4xl tracking-widest">
        {mm}:{ss}
      </div>

      <div className="flex w-full max-w-xs items-center gap-2">
        <Button
          onClick={() => setRunning((r) => !r)}
          className="flex-1 rounded-full bg-primary text-primary-foreground glow-primary"
          size="lg"
        >
          {running ? (
            <>
              <Pause className="h-4 w-4" /> {strings.levels.common.pause}
            </>
          ) : (
            <>
              <Play className="h-4 w-4" />{' '}
              {remaining === KEGEL_DURATION_S
                ? strings.levels.common.begin
                : strings.levels.common.resume}
            </>
          )}
        </Button>
        <Button
          onClick={reset}
          variant="outline"
          size="lg"
          className="rounded-full"
          aria-label={strings.levels.common.resetAria}
        >
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function TechniquePhase({ onAdvance }: { onAdvance: () => void }) {
  const { strings } = useStrings();
  const haptic = useHaptic();
  const [idx, setIdx] = useState(0);
  const t = TECHNIQUES[idx];

  return (
    <>
      <div className="text-center">
        <h4 className="font-serif text-xl">{t.name}</h4>
        <p className="mt-1 max-w-xs text-sm text-muted-foreground">{t.description}</p>
      </div>

      <div className="relative flex h-64 w-64 items-center justify-center">
        {t.id === 'orbit' ? <OrbitIllustration /> : <FlatHandIllustration />}
      </div>

      <Card className="w-full max-w-sm border-border bg-card p-4">
        <ol className="space-y-2.5">
          {t.steps.map((step, i) => (
            <li key={i} className="flex gap-3 text-sm">
              <span className="font-serif text-primary">{i + 1}.</span>
              <span className="text-foreground/90">{step}</span>
            </li>
          ))}
        </ol>
      </Card>

      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          className="rounded-full"
          onClick={() => setIdx((i) => (i + 1) % TECHNIQUES.length)}
        >
          {strings.levels.technique.swapCta}
        </Button>
      </div>

      <Button
        onClick={() => {
          haptic.tap();
          onAdvance();
        }}
        size="lg"
        className="rounded-full bg-primary px-8 text-primary-foreground glow-primary"
      >
        {strings.levels.pelvicPower.advanceCta}
      </Button>
    </>
  );
}
